import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DetailsListLayoutMode,
  IColumn,
  IDetailsRowProps,
  IDetailsRowStyles,
  MessageBar,
  MessageBarType,
  SelectionMode,
  ShimmeredDetailsList,
} from "@fluentui/react";
import { isEmpty, orderBy } from "lodash-es";

import { CatalogCollection } from "./Catalog.Collection";
import { useCollections } from "utils/requests";
import { IPcCollection, IStacCollection } from "types/stac";
import { getCollectionColumns } from "./Catalog.Collection.Columns";
import { ISortBy } from "pages/Explore/types";
import { useCollectionFilter } from "./collectionFilter";
import { NoResults } from "./Catalog.NoResults";
import { CatalogCollectionSuggestion } from "./Catalog.Collection.Suggestion";

interface CatalogCollectionListProps {
  filterText: string;
  setFilterText: (filterText: string) => void;
  preFilterCollectionFn?: (collection: IStacCollection) => boolean;
  itemsAsButton?: boolean;
  onButtonClick?: (collectionId: string) => void;
  onEdit?: (collection: IStacCollection) => void;
  isEditable?: boolean;
  handleNoResultCreateButton?: () => void;
}

export const CatalogCollectionTable: React.FC<CatalogCollectionListProps> = ({
  filterText,
  setFilterText,
  preFilterCollectionFn = defaultTrueFn,
  itemsAsButton = false,
  onButtonClick = defaultVoidFn,
  isEditable = true,
  onEdit,
  handleNoResultCreateButton: handleButtonClick = defaultVoidFn,
}) => {
  const [sortInfo, setSortInfo] = useState<ISortBy>(createdSort);
  const [debouncedFilterText, setDebouncedFilterText] = useState(filterText);
  const { isLoading, isError, error, data } = useCollections();
  const { search, suggest } = useCollectionFilter(
    data?.collections.filter(preFilterCollectionFn) || []
  );

  // Debounce the filter text so the filtered results (and the "No Results"
  // live-region announcement) only update after the user pauses typing.
  useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedFilterText(filterText),
      noResultsAnnounceDelayMs
    );
    return () => window.clearTimeout(handle);
  }, [filterText]);

  const filteredCollections = useMemo(() => {
    const filtered = search(debouncedFilterText);
    return orderBy(filtered, sortInfo.field, sortInfo.direction);
  }, [debouncedFilterText, search, sortInfo]);

  const handleCollectionCardRender = useCallback(
    (item: IPcCollection) => {
      return (
        <CatalogCollection
          collection={item}
          onKeywordClick={setFilterText}
          asButton={itemsAsButton}
          onButtonClick={onButtonClick}
        />
      );
    },
    [itemsAsButton, onButtonClick, setFilterText]
  );

  const handleColumnClick = useCallback(
    (ev: any, column?: IColumn) => {
      if (!column) return;
      const fieldNames = [sortInfo.field, createdSort.field, updatedSort.field];

      if (fieldNames.includes(column.key)) {
        const matchingSort = [sortInfo, createdSort, updatedSort].find(
          sort => sort.field === column.key
        );
        const directionField = matchingSort ? matchingSort.direction : null;
        setSortInfo({
          field: column.key,
          direction: directionField === "asc" ? "desc" : "asc",
        });
      } else {
        setSortInfo({
          field: column.fieldName || "title",
          direction: "asc",
        });
      }
    },
    [sortInfo]
  );

  const columns = useMemo(() => {
    return getCollectionColumns(
      handleCollectionCardRender,
      itemsAsButton,
      onButtonClick,
      sortInfo,
      isEditable,
      onEdit
    );
  }, [
    handleCollectionCardRender,
    onEdit,
    sortInfo,
    isEditable,
    onButtonClick,
    itemsAsButton,
  ]);

  const hasResults = !isEmpty(filteredCollections) && !isLoading;
  const suggestion = !hasResults ? suggest(debouncedFilterText) : undefined;

  // Announce result counts + first few collection names to screen readers
  // whenever the debounced filter text changes. Gated on a ref so that
  // sort/direction changes (which mutate filteredCollections order) don't
  // re-fire the same announcement.
  const [resultsAnnouncement, setResultsAnnouncement] = useState("");
  const lastAnnouncedFilter = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading) return;
    if (!debouncedFilterText) {
      lastAnnouncedFilter.current = null;
      setResultsAnnouncement("");
      return;
    }
    if (lastAnnouncedFilter.current === debouncedFilterText) return;
    lastAnnouncedFilter.current = debouncedFilterText;
    if (!hasResults) return; // NoResults live region handles this case.
    const count = filteredCollections.length;
    const names = filteredCollections
      .slice(0, maxAnnouncedResultNames)
      .map(c => c.title || c.id)
      .join(", ");
    const suffix =
      count > maxAnnouncedResultNames
        ? `, and ${count - maxAnnouncedResultNames} more`
        : "";
    setResultsAnnouncement(
      `${count} result${
        count === 1 ? "" : "s"
      } for "${debouncedFilterText}": ${names}${suffix}.`
    );
  }, [debouncedFilterText, isLoading, hasResults, filteredCollections]);

  return (
    <div style={containerStyles}>
      {isError && (
        <div>
          <MessageBar isMultiline messageBarType={MessageBarType.error}>
            {error.code} - {error.message}
            {error.response && typeof error.response.data == "string" && (
              <div>{error.response.data as string}</div>
            )}
          </MessageBar>
        </div>
      )}
      {!isError && (
        <ShimmeredDetailsList
          columns={columns}
          items={filteredCollections}
          isHeaderVisible={true}
          selectionMode={SelectionMode.none}
          styles={listStyles}
          layoutMode={DetailsListLayoutMode.justified}
          getKey={getItemKey}
          enableShimmer={isLoading}
          onRenderRow={handleRowRender}
          onColumnHeaderClick={handleColumnClick}
          data-cy="catalog-collection-table"
          ariaLabelForShimmer="Loading collections"
        />
      )}
      {suggestion && (
        <CatalogCollectionSuggestion
          text={suggestion.suggestion}
          setFilterText={setFilterText}
        />
      )}
      {!isError && !hasResults && !isLoading && (
        <NoResults
          typeText={debouncedFilterText}
          handleCreateOpen={handleButtonClick}
        />
      )}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={visuallyHiddenStyle}
      >
        {resultsAnnouncement}
      </div>
    </div>
  );
};

const getItemKey = (item: IStacCollection) => item?.id;

// Custom row renderer to remove hover effect
const handleRowRender = (
  props?: IDetailsRowProps,
  defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null
) => {
  if (!props) return null;
  return <div>{defaultRender?.({ ...props, styles: rowStyles })}</div>;
};

const createdSort: ISortBy = {
  field: "msft:_created",
  direction: "desc",
};

const updatedSort: ISortBy = {
  field: "msft:_updated",
  direction: "asc",
};

const defaultVoidFn = () => {};
const defaultTrueFn = () => true;

// Delay before applying the typed filter; also throttles the "No Results"
// screen-reader announcement so it doesn't fire on every keystroke.
const noResultsAnnounceDelayMs = 400;

// Cap the number of collection names read aloud so long result lists don't
// overwhelm screen-reader users.
const maxAnnouncedResultNames = 5;

const containerStyles = {
  width: "100%",
};

const listStyles = {
  root: {
    width: "100%",
  },
};

const rowStyles: Partial<IDetailsRowStyles> = {
  root: {
    "&:hover": {
      background: "unset",
    },
  },
};

const visuallyHiddenStyle: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};
